import { useState } from "react";
import { ChevronDown, Phone } from "lucide-react";

/*
 * LocationFAQ — Section 9/10 for /garage-door-repair-hills-district/
 * MASTER TEMPLATE — duplicate for other locations, change only the LOC block.
 * Paste this ENTIRE file into src/pages/GarageDoorRepairHillsDistrict/sections/LocationFAQ.tsx
 */

// ╔══════════════════════════════════════════════════════════════════╗
// ║  LOC CONFIG — change ONLY this block when duplicating          ║
// ╚══════════════════════════════════════════════════════════════════╝
const LOC = {
  name: "Hills District",
  h2Span: "Hills District",
  subtitle: "Common questions from homeowners across Castle Hill, Kellyville, Rouse Hill and surrounding suburbs.",
  ctaLabel: "Still have questions? Call us",
  faqs: [{
    q: "How fast can you get to the Hills District?",
    a: "We use the M2 and NorthConnex to reach The Hills quickly — most suburbs are 35–50 minutes from our base. Same-day service when booked before noon."
  }, {
    q: "Do you handle oversized double and triple garage doors?",
    a: "Absolutely — oversized doors are our bread and butter in The Hills. We carry heavy-duty springs, high-cycle motors and wide panels rated for double and triple garages."
  }, {
    q: "Do you service new-build estates like North Kellyville and The Ponds?",
    a: "Yes. We install and repair garage doors across all new-build estates in The Hills including North Kellyville, Tallawong, The Ponds, Stanhope Gardens and Beaumont Hills."
  }, {
    q: "What brands do you service in the Hills District?",
    a: "All major brands — B&D, Merlin, Chamberlain, ATA, Steel-Line, Gliderol, Centurion, LiftMaster, Marantec, Grifco and Hormann. Parts carried on the van."
  }, {
    q: "How much does a garage door repair cost in the Hills District?",
    a: "Spring replacements typically run $250–$450, motor repairs $180–$350, and cable repairs from $150. We give fixed-price quotes over the phone — no surprises."
  }, {
    q: "Do you service acreage properties in Dural and Galston?",
    a: "Yes — we regularly service rural and semi-rural properties in Dural, Galston, Arcadia and Kenthurst. Larger properties often have heavier commercial-grade doors — we handle those too."
  }, {
    q: "Is the 1-Year Labour Warranty valid in the Hills District?",
    a: "Yes — our 1-Year Labour Warranty covers every job in every Hills District suburb. Parts carry the manufacturer\'s warranty on top."
  }, {
    q: "Can I get an emergency repair in the Hills District?",
    a: "Yes. We offer priority emergency response across all Hills District suburbs. Call 0485 000 933 and we\'ll give you an ETA within 15 minutes."
  }]
};
// ╚══════════════════════════════════════════════════════════════════╝

const GOLD = "#F5A623";
const NAVY = "#081438";
const NAVY2 = "#0f2347";
const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: LOC.faqs.map(f => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: f.a
    }
  }))
};
export default function LocationFAQ() {
  const [open, setOpen] = useState<number | null>(0);
  return <section id="location-faq" style={{
    background: NAVY,
    position: "relative",
    overflow: "hidden"
  }}>

      {/* Wave top — DUPLICATE from LocationReviews bottom */}
      <div style={{
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,60 C360,0 1080,0 1440,60 L1440,0 L0,0 Z" fill="#FAF7F1" />
        </svg>
      </div>

      {/* JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(FAQ_SCHEMA)
    }} />

      {/* Grain */}
      <svg aria-hidden="true" style={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      opacity: 0.05,
      pointerEvents: "none",
      zIndex: 1
    }}>
        <filter id="lfq-grain"><feTurbulence type="fractalNoise" baseFrequency="0.65" numOctaves="3" stitchTiles="stitch" /><feColorMatrix type="saturate" values="0" /></filter>
        <rect width="100%" height="100%" filter="url(#lfq-grain)" />
      </svg>

      {/* Scanlines */}
      <div aria-hidden="true" style={{
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg,transparent,${GOLD}60,transparent)`,
      zIndex: 2
    }} />
      <div aria-hidden="true" style={{
      position: "absolute",
      bottom: 0,
      left: 0,
      right: 0,
      height: "1px",
      background: `linear-gradient(90deg,transparent,${GOLD}60,transparent)`,
      zIndex: 2
    }} />

      <div style={{
      position: "relative",
      zIndex: 3,
      maxWidth: "1280px",
      margin: "0 auto",
      padding: "clamp(56px,7vw,88px) clamp(20px,5vw,48px)"
    }}>

        {/* Two-column */}
        <div style={{
        display: "grid",
        gridTemplateColumns: "1fr",
        gap: "48px"
      }} className="lg:!grid-cols-[1fr_2fr]">

          {/* Left — sticky */}
          <div className="lg:sticky lg:top-28 lg:self-start">
            <div style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "20px"
          }}>
              <div style={{
              width: "32px",
              height: "2px",
              background: GOLD
            }} />
              <span style={{
              fontFamily: "Manrope,sans-serif",
              fontSize: "11px",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: `${GOLD}BB`
            }}>
                FAQs
              </span>
            </div>
            <h2 style={{
            fontFamily: "Plus Jakarta Sans,sans-serif",
            fontWeight: 800,
            fontSize: "clamp(26px,3.5vw,42px)",
            lineHeight: 1.1,
            color: "#FFFFFF",
            marginBottom: "16px"
          }}>
              {LOC.h2Span}<br /><span style={{
              color: GOLD
            }}>Questions</span>
            </h2>
            <p style={{
            fontFamily: "Manrope,sans-serif",
            fontSize: "clamp(14px,1.5vw,16px)",
            color: "rgba(255,255,255,0.6)",
            lineHeight: 1.7,
            marginBottom: "28px"
          }}>
              {LOC.subtitle}
            </p>
            <a href="tel:0485000933" style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            background: GOLD,
            color: NAVY,
            padding: "13px 24px",
            borderRadius: "8px",
            fontFamily: "Manrope,sans-serif",
            fontWeight: 700,
            fontSize: "14px",
            textDecoration: "none"
          }}>
              {/** @preserve lucide-do-not-replace */}
              <span data-icon-container="lucide-do-not-replace"><Phone size={16} color={NAVY} strokeWidth={2.5} /></span>
              {LOC.ctaLabel}
            </a>
          </div>

          {/* Right — accordion */}
          <div style={{
          display: "flex",
          flexDirection: "column",
          gap: "10px"
        }}>
            {LOC.faqs.map((faq, i) => {
            const isOpen = open === i;
            return <div key={i} style={{
              background: isOpen ? NAVY2 : "rgba(255,255,255,0.03)",
              border: `1px solid ${isOpen ? `${GOLD}40` : "rgba(255,255,255,0.08)"}`,
              borderRadius: "10px",
              overflow: "hidden",
              transition: "border-color 0.2s"
            }}>
                  <button onClick={() => setOpen(isOpen ? null : i)} style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
                padding: "18px 20px",
                background: "none",
                border: "none",
                cursor: "pointer",
                textAlign: "left"
              }}>
                    <span style={{
                  fontFamily: "Plus Jakarta Sans,sans-serif",
                  fontWeight: 600,
                  fontSize: "15px",
                  color: isOpen ? GOLD : "#FFFFFF",
                  lineHeight: 1.3,
                  flex: 1
                }}>
                      {faq.q}
                    </span>
                    {/** @preserve lucide-do-not-replace */}
                    <span data-icon-container="lucide-do-not-replace" style={{
                  flexShrink: 0,
                  transform: isOpen ? "rotate(180deg)" : "rotate(0)",
                  transition: "transform 0.2s"
                }}>
                      <ChevronDown size={18} color={isOpen ? GOLD : "rgba(255,255,255,0.4)"} strokeWidth={2} />
                    </span>
                  </button>
                  {isOpen && <div style={{
                padding: "0 20px 18px",
                fontFamily: "Manrope,sans-serif",
                fontSize: "14px",
                color: "rgba(255,255,255,0.7)",
                lineHeight: 1.75
              }}>
                      {faq.a}
                    </div>}
                </div>;
          })}
          </div>
        </div>
      </div>

      {/* Wave bottom — DUPLICATE in LocationCTA top */}
      <div style={{
      position: "relative",
      zIndex: 3,
      lineHeight: 0
    }}>
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" style={{
        width: "100%",
        display: "block"
      }}>
          <path d="M0,0 C360,60 1080,60 1440,0 L1440,60 L0,60 Z" fill="#FAF7F1" />
        </svg>
      </div>
    </section>;
}